import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlay, FaStop, FaSync } from 'react-icons/fa';
import { KpiAdminService } from '../../api/kpi/KpiAdminService';
import './KpiUpdate.css';

export default function KpiUpdate() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState('2024-09-01');
  const [endDate, setEndDate] = useState('2026-03-06');
  const [progress, setProgress] = useState(null);
  const [polling, setPolling] = useState(false);
  const logEndRef = useRef(null);
  const timerRef = useRef(null);

  const fetchProgress = useCallback(async () => {
    try {
      const data = await KpiAdminService.updateProgress();
      setProgress(data);
      if (data.running) {
        setPolling(true);
      } else if (polling && data.phase) {
        setPolling(false);
      }
    } catch (e) {
      console.error(e);
    }
  }, [polling]);

  // 초기 로드 + 폴링
  useEffect(() => {
    fetchProgress();
  }, []);

  useEffect(() => {
    if (polling) {
      timerRef.current = setInterval(fetchProgress, 1500);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [polling, fetchProgress]);

  // 로그 자동 스크롤
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [progress?.log?.length]);

  const handleStart = async () => {
    try {
      await KpiAdminService.updateStart(startDate, endDate);
      setPolling(true);
      fetchProgress();
    } catch (e) {
      alert('시작 실패: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleStop = async () => {
    try {
      await KpiAdminService.updateStop();
    } catch (e) {
      console.error(e);
    }
  };

  const running = progress?.running;
  const stats = progress?.stats || {};
  const totalWork = (progress?.total_dates || 0) * (progress?.total_companies || 0);
  const doneWork = ((progress?.processed_dates || 0) * (progress?.total_companies || 0))
    + (progress?.date_companies_done || 0);
  const pct = totalWork > 0 ? Math.min(100, Math.round(doneWork / totalWork * 100)) : 0;

  const elapsed = progress?.started_at
    ? Math.round((new Date() - new Date(progress.started_at)) / 1000)
    : 0;
  const eta = pct > 0 && pct < 100
    ? Math.round(elapsed / pct * (100 - pct))
    : 0;

  const fmtTime = (sec) => {
    if (!sec) return '-';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) return `${h}시간 ${m}분`;
    if (m > 0) return `${m}분 ${s}초`;
    return `${s}초`;
  };

  return (
    <div className="kpi-update-page">
      <div className="kpi-update-header">
        <button className="back-btn" onClick={() => navigate('/admin/kpi')}>
          <FaArrowLeft /> KPI 관리
        </button>
        <h2>KPI 사이트 데이터 정합성 검증</h2>
      </div>

      {/* 설정 영역 */}
      <div className="update-config">
        <div className="config-row">
          <label>시작일</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                 disabled={running} />
          <label>종료일</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                 disabled={running} />
          {!running ? (
            <button className="start-btn" onClick={handleStart}>
              <FaPlay /> 시작
            </button>
          ) : (
            <button className="stop-btn" onClick={handleStop}>
              <FaStop /> 중지
            </button>
          )}
          {!running && progress?.phase && (
            <button className="refresh-btn" onClick={fetchProgress}>
              <FaSync />
            </button>
          )}
        </div>
        <div className="config-hint">
          2024년 업체: 2024-09-01 ~ 2026-03-06 / 2025년 업체: 2025-09-01 ~ 2026-03-06
        </div>
      </div>

      {/* 진행 상황 */}
      {progress?.phase && (
        <div className="update-progress">
          <div className="progress-header">
            <span className={`phase-badge ${running ? 'running' : 'done'}`}>
              {progress.phase}
            </span>
            {running && (
              <span className="progress-detail">
                {progress.current_date} — {progress.current_company}
              </span>
            )}
          </div>

          <div className="progress-bar-wrap">
            <div className="progress-bar" style={{ width: `${pct}%` }} />
            <span className="progress-pct">{pct}%</span>
          </div>

          <div className="progress-meta">
            <span>날짜: {progress.processed_dates}/{progress.total_dates}</span>
            <span>업체: {progress.total_companies}개</span>
            <span>경과: {fmtTime(elapsed)}</span>
            {running && eta > 0 && <span>예상 잔여: {fmtTime(eta)}</span>}
          </div>

          {/* 통계 */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-num">{stats.checked || 0}</div>
              <div className="stat-label">확인</div>
            </div>
            <div className="stat-card pass">
              <div className="stat-num">{stats.pass || 0}</div>
              <div className="stat-label">통과(값 저장)</div>
            </div>
            <div className="stat-card dup">
              <div className="stat-num">{stats.duplicate || 0}</div>
              <div className="stat-label">사이트중복</div>
            </div>
            <div className="stat-card nodata">
              <div className="stat-num">{stats.no_data || 0}</div>
              <div className="stat-label">데이터없음</div>
            </div>
          </div>
        </div>
      )}

      {/* 중복 목록 */}
      {progress?.duplicates?.length > 0 && (
        <div className="dup-section">
          <h3>사이트 중복 적재 목록 ({progress.duplicates.length}건)</h3>
          <div className="dup-table-wrap">
            <table className="dup-table">
              <thead>
                <tr><th>업체</th><th>날짜</th><th>상세</th></tr>
              </thead>
              <tbody>
                {progress.duplicates.map((d, i) => (
                  <tr key={i}>
                    <td>{d.company_name} ({d.company_seq})</td>
                    <td>{d.date}</td>
                    <td><span className="dup-tag">{d.reason}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 로그 */}
      {progress?.log?.length > 0 && (
        <div className="log-section">
          <h3>실행 로그</h3>
          <div className="log-box">
            {progress.log.map((line, i) => (
              <div key={i} className="log-line">{line}</div>
            ))}
            <div ref={logEndRef} />
          </div>
        </div>
      )}
    </div>
  );
}
